/* eslint-disable no-underscore-dangle */
import { Box, FlexLayout, H4, Icon } from '@deity/falcon-ui';
import type { IFlowType, IRestEndpointConfig } from '@deity-io/di-data-flows';
import { availableFlowTypes } from '@deity-io/di-data-flows';
import type { AutocompleteOption } from '@deity-io/portal-ui';
import { Input, Select } from '@deity-io/portal-ui';
import { CircularProgress } from '@mui/material';
import { Tooltip } from 'components/Tooltip';
import { CACHE_FIRST, ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  useGetEndpointsByPackageVersionLazyQuery,
  useGetFlowsByPackageVersionLazyQuery,
  useGetMappingsByPackageVersionLazyQuery,
  useGetModelsByPackageVersionLazyQuery,
  useGetWebServicesByPackageVersionLazyQuery
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import EndpointDetails from './EndpointDetails';

// TODO: This component will be replace when an entity search API IS available.

interface IEntityPickerProps {
  isEditable: boolean;
  property: string;
  description?: string;
  entityUUID?: string;
  entity: 'mapping' | 'endpoint' | 'structure' | 'anyFlow' | 'webService' | IFlowType;
  label?: string;
  name?: string;
  error?: boolean;
  showEntityLink?: boolean;
  onChange: (key: string, entityId: string) => void;
}
const EntityPicker: FC<IEntityPickerProps> = ({
  isEditable,
  property,
  description,
  entityUUID,
  entity,
  onChange,
  label,
  name,
  error,
  showEntityLink = false
}) => {
  const { pathname } = useLocation();
  const params: any = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [getAllMappings, { data: mappingsData, loading: loadingMappings }] = useGetMappingsByPackageVersionLazyQuery({
    ...CACHE_FIRST
  });
  const [getAllWebServices, { data: WebServicesData, loading: loadingWebServices }] =
    useGetWebServicesByPackageVersionLazyQuery({
      ...CACHE_FIRST
    });
  const [getAllEndpoint, { data: endpointsData, loading: loadingEndpoints }] = useGetEndpointsByPackageVersionLazyQuery(
    {
      ...CACHE_FIRST
    }
  );
  const [getAllStructures, { data: structuresData, loading: loadingStructures }] =
    useGetModelsByPackageVersionLazyQuery({
      ...CACHE_FIRST
    });

  const [getAllFlows, { data: flowData, loading: loadingFlows }] = useGetFlowsByPackageVersionLazyQuery({
    ...CACHE_FIRST
  });

  const variables = {
    offset: 0,
    limit: 9999,
    packageVersionUuid: params.versionUuid
  };
  const allFlowTypes = availableFlowTypes();
  const isFound = allFlowTypes.find(item => item === entity);
  if (isFound) {
    variables['flowType'] = isFound;
    entity = 'flow';
  }

  const ENTITY_QUERY = {
    endpoint: {
      loading: loadingEndpoints,
      data: endpointsData?.getEndpointsByPackageVersion.data
    },
    webService: {
      loading: loadingWebServices,
      data: WebServicesData?.getWebServicesByPackageVersion.data
    },
    mapping: { loading: loadingMappings, data: mappingsData?.getMappingsByPackageVersion.data },
    structure: { loading: loadingStructures, data: structuresData?.getModelsByPackageVersion.data },
    flow: { loading: loadingFlows, data: flowData?.getFlowsByPackageVersion.data as Array<any> } // this cast is because i added fields to the flow. and then i cant do array operations anymore
  };

  useEffect(() => {
    const GET = {
      endpoint: () => getAllEndpoint({ variables }),
      mapping: () => getAllMappings({ variables }),
      structure: () => getAllStructures({ variables }),
      flow: () => getAllFlows({ variables }),
      webService: () => getAllWebServices({ variables })
    };

    if (params) {
      GET[entity]();
    }
  }, []);

  const currentEntity = ENTITY_QUERY[entity].data?.find(e => e.uuid === entityUUID);
  // set error if current entity not loaded
  error =
    error ||
    (!ENTITY_QUERY[entity].loading && entityUUID !== undefined && entityUUID.trim().length > 0 && !currentEntity);

  return (
    <FlexLayout p="xs" mt="xs" mb="xs" bg="panelLight" borderRadius="small" css={{ minHeight: '78px' }} width="full">
      <FlexLayout alignItems="center" width="full">
        <FlexLayout alignItems="center" width="full" flexDirection="row" justifyContent="space-between" mb="xs">
          <H4 fontWeight="bold" mr="xs" style={{ textTransform: 'capitalize' }}>
            {name || property}
          </H4>
          <FlexLayout flexDirection="row" alignItems="center" mb="xs">
            {showEntityLink && entityUUID && (
              <Tooltip key="tt_add" content={`Open ${entity}`} direction="left">
                <Link to={`/packages/${params.packageId}/${params.versionUuid}/${entity}s/${entityUUID}`}>
                  <Icon src="openInNew" color="secondary" stroke="transparent" />
                </Link>
              </Tooltip>
            )}
            {description && (
              <Tooltip key="tt_description" content={description} direction="left">
                <Icon src="helpOutline" color="secondary" stroke="transparent" />
              </Tooltip>
            )}
          </FlexLayout>
        </FlexLayout>
        <Box width="full">
          <Select
            label={entity}
            sx={{ marginY: 1 }}
            onChange={(val: any) => onChange(property, val?.value)}
            options={(ENTITY_QUERY[entity].data || []).map(e => ({
              value: e.uuid,
              label: entity === 'endpoint' ? `${e.WebService?.name} ${e.name}` : e.name
            }))}
            value={
              currentEntity?.name
                ? ({
                    value: entityUUID,
                    label:
                      entity === 'endpoint'
                        ? `${currentEntity?.WebService?.name} ${currentEntity?.name}`
                        : currentEntity?.name || ''
                  } as AutocompleteOption)
                : null
            }
            loading={ENTITY_QUERY[entity].loading}
            renderInput={p => (
              <Input
                {...p}
                label={label || entity.toUpperCase()}
                error={error}
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>
                      {ENTITY_QUERY[entity].loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {p.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            disabled={!isEditable}
          />
        </Box>
        {entity === 'endpoint' && currentEntity?.schema && (
          <EndpointDetails
            schema={currentEntity.schema as IRestEndpointConfig}
            service={currentEntity.WebService.schema as IRestEndpointConfig}
          />
        )}
      </FlexLayout>
    </FlexLayout>
  );
};
export default EntityPicker;
